import styled, { css } from 'styled-components';
import { Typography } from '../../../global/theme';
import { desktop_small, mobile_xl } from '../../../global/mediaQueryMixin';
import { AlignCenter, FlexBetween, HideScrollbar, LineClamp } from '../../../global/commonStyle';

interface InputLabelProps {
  taxLength?: number;
}
interface BottomFieldProps {
  taxFieldCount?: number;
}

const OuterDiv = styled.div`
  max-width: 1022px;
  margin: auto;
  height: 100dvh;
  padding-inline: var(--space-24);
  ${HideScrollbar}
  ${mobile_xl(css`
    padding-inline: var(--space-20);
  `)}
`;

const Group = styled.div`
  box-orient: vertical;
  flex-direction: column;
  display: flex;
  ${mobile_xl(css`
    margin-top: var(--space-8);
  `)}
`;

const HeaderBlock = styled.div`
  ${FlexBetween}
  padding-block: var(--space-10);
  ${mobile_xl(css`
    display: none;
  `)}
`;
const Heading = styled.div`
  ${AlignCenter}
  gap: var(--space-12);
`;
const Icon = styled.div`
  display: inline-flex;
  .invoice-icon {
    fill: var(--brand-primary);
  }
`;
const HeaderTitle = styled.h1`
  ${Typography.heading_md}
  color: var(--text-primary);
  margin: var(--space-0);
  ${mobile_xl(css`
    ${LineClamp(1)}
    word-break: break-all;
  `)}
`;

const MainGroup = styled.form`
  width: 100%;
  margin: var(--space-0);
  display: flex;
  flex-direction: column;
  gap: var(--space-24);
  padding-bottom: var(--space-14);
  ${mobile_xl(css`
    padding-bottom: var(--space-96);
  `)}
`;
const Group1 = styled.div`
  background: var(--background-primary);
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-16);
  display: flex;
  flex-direction: column;
  gap: var(--space-20);
  padding: var(--space-16);
  ${mobile_xl(css`
    padding: var(--space-12);
  `)}
`;
const Row = styled.div`
  display: flex;
  gap: var(--space-20);
  flex-wrap: wrap;
  ${mobile_xl(css`
    flex-direction: column;
  `)}
`;

const InputLabel = styled.div<InputLabelProps>`
  max-width: 172px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: var(--space-0);
  gap: var(--space-6);
  color: var(--text-primary);

  .date-picker {
    input {
      width: 100%;
      min-width: unset;
      /* Use CSS custom property for dynamic width based on tax length */
      --tax-field-width: ${({ taxLength }) => (taxLength === 2 ? '178px' : '206px')};
      max-width: var(--tax-field-width);
    }
  }
  ${mobile_xl(css`
    max-width: 100%;
  `)}
`;
const Label = styled.p`
  ${Typography.body_md_medium}
  color: var(--text-secondary);
  margin: var(--space-0);
`;
const Label1 = styled.p`
  ${Typography.body_md_medium}
  color: var(--text-secondary);
  margin: var(--space-0);
  max-width: 466px;
  min-width: 148px;
  width: 100%;
  padding-right: var(--space-12);
  text-transform: uppercase;
`;

const NoTex = styled.p`
  ${Typography.body_md_medium}
  color: var(--brand-primary);
  cursor: pointer;
  margin: var(--space-0);
  white-space: nowrap;
`;
const AnotherTex = styled.p<{ showBorder?: boolean }>`
  ${Typography.body_md_medium}
  color: var(--brand-primary);
  padding-left: ${({ showBorder }) => (showBorder ? 'var(--space-8)' : 'var(--space-0)')};
  box-shadow: ${({ showBorder }) => (showBorder ? `var(--left-border)` : 'none')};
  cursor: pointer;
  margin: var(--space-0);
`;

const TaxOutside = styled.div`
  display: flex;
  gap: var(--space-8);
  align-items: flex-end;
`;

const Group2 = styled.div`
  background: var(--background-primary);
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-16);
  ${mobile_xl(css`
    padding: var(--space-12);
  `)}
`;
const GroupHeader = styled.div`
  padding: var(--space-10) var(--space-12) var(--space-10) var(--space-24);
  justify-content: space-between;
  display: flex;
  border-bottom: 0.5px solid var(--border-primary);
  background-color: var(--background-tertiary);
  border-radius: var(--border-radius-16) var(--border-radius-16) var(--border-radius-0) var(--border-radius-0);
  ${desktop_small(css`
    width: max-content;
    padding-right: var(--space-0);
  `)}
  ${mobile_xl(css`
    display: none;
  `)}
`;
const RightHeader = styled.div`
  ${AlignCenter}
`;
const Project = styled.p`
  ${Typography.body_md_medium}
  color: var(--text-secondary);
  border-right: 0.5px solid var(--border-primary);
  padding-right: var(--space-36);
  margin: var(--space-0);
  text-transform: uppercase;
  white-space: nowrap;
  ${mobile_xl(css`
    border-right: none;
    padding: var(--space-0);
  `)}
`;
const Quantity = styled.p`
  ${Typography.body_md_medium}
  color: var(--text-secondary);
  border-right: 0.5px solid var(--border-primary);
  padding-left: var(--space-12);
  margin: var(--space-0);
  text-transform: uppercase;
  width: 50px;
  text-align: center;
  ${mobile_xl(css`
    border-right: none;
    padding: var(--space-0);
    width: unset;
  `)}
`;
const Tax = styled.p`
  ${Typography.body_md_medium}
  color: var(--text-secondary);
  border-right: 0.5px solid var(--border-primary);
  padding-inline: var(--space-12);
  margin: var(--space-0);
  text-transform: uppercase;
  width: 35px;
  text-align: center;
  ${mobile_xl(css`
    border-right: none;
    padding: var(--space-0);
  `)}
`;
const Amount = styled.p`
  ${Typography.body_md_medium}
  color: var(--text-secondary);
  padding-left: var(--space-12);
  margin: var(--space-0);
  text-transform: uppercase;
  width: 100px;
  text-align: end;
  ${desktop_small(css`
    padding-right: var(--space-12);
  `)}
  ${mobile_xl(css`
    padding-inline: var(--space-0);
    text-align: left;
    width: unset;
  `)}
`;
const Fields = styled.div<{ isDragging?: boolean }>`
  padding: var(--space-8) var(--space-12) var(--space-8) var(--space-24);
  ${FlexBetween}
  position: relative;
  user-select: none;
  cursor: auto;
  background: ${(props) => (props.isDragging ? 'var(--background-primary)' : 'none')};
  border-top: ${(props) => (props.isDragging ? '0.5px solid var(--border-primary)' : 'none')};
  border-bottom: 0.5px solid var(--border-primary);

  &:hover {
    .drag-icon {
      visibility: visible;
    }
  }
  ${desktop_small(css`
    padding: var(--space-0) var(--space-0) var(--space-0) var(--space-24);
    width: max-content;
  `)}
  ${mobile_xl(css`
    width: unset;
    flex-direction: column;
    border: 0.5px solid var(--border-primary);
    border-radius: var(--border-radius-8);
    margin-bottom: var(--space-24);
    padding: var(--space-0);
  `)}
`;
const InputArea = styled.input`
  min-width: 148px;
  max-width: 454px;
  width: 100%;
  ${Typography.body_md_medium}
  color: var(--text-primary);
  background: var(--background-primary);
  border: none;
  padding-right: var(--space-8);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-family: 'Inter';
  &:focus {
    outline: none;
  }
  ::placeholder {
    color: var(--text-placeholder);
    ${Typography.body_md_medium};
  }
  ${mobile_xl(css`
    width: -webkit-fill-available;
    padding: var(--space-8);
    border-bottom: 0.5px solid var(--border-primary);
    border-radius: var(--border-radius-8) var(--border-radius-8) var(--border-radius-0) var(--border-radius-0);
    ::placeholder {
      color: var(--text-secondary);
    }
  `)}
`;

const QuantityRate = styled.input`
  ${Typography.body_md_medium}
  color: var(--text-primary);
  background: var(--background-primary);
  border: 0;
  border-radius: var(--border-radius-4);
  margin-left: var(--space-16);
  width: 46px;
  text-align: center;
  padding: var(--space-0);
  &:focus-visible {
    outline: none;
  }
  ::placeholder {
    color: var(--text-placeholder);
    ${Typography.body_md_medium}
  }
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: var(--space-0);
  }
  ${mobile_xl(css`
    margin-left: var(--space-0);
    text-align: end;
  `)}
`;

const AmountRate = styled.div`
  width: 100px;
  ${Typography.body_md_medium}
  color: var(--text-primary);
  margin-inline: var(--space-12);
  text-align: end;
  ::placeholder {
    ${Typography.body_md_medium}
  }
  ${mobile_xl(css`
    margin: var(--space-0);
    width: unset;
  `)}
`;

const AddButton = styled.div`
  ${AlignCenter}
  width: max-content;
  margin: var(--space-80) var(--space-0) var(--space-12) var(--space-16);
  ${mobile_xl(css`
    margin: var(--space-24) var(--space-0) var(--space-14);
  `)}
`;

const BottomField = styled.div<BottomFieldProps>`
  display: flex;
  justify-content: space-between;
  margin: var(--space-0) var(--space-16) var(--space-16);
  align-items: flex-start;
  gap: var(--space-12);
  /* Use CSS custom property for textarea height calculation */
  --textarea-height: ${({ taxFieldCount }) => {
    if (taxFieldCount === 0) return '104px';
    if (taxFieldCount === 1) return '132px';
    if (taxFieldCount === 2) return '160px';
    return 'auto';
  }};
  textarea {
    height: var(--textarea-height);
  }
  ${mobile_xl(css`
    flex-direction: column-reverse;
    gap: var(--space-14);
    margin: var(--space-0);
  `)}
`;

const Total = styled.div`
  background: var(--background-tertiary);
  box-shadow: var(--border);
  border-radius: var(--border-radius-8);
  padding: var(--space-14);
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  width: 237px;
  height: auto;
  ${mobile_xl(css`
    width: -webkit-fill-available;
  `)}
`;
const Subtotal = styled.div`
  ${FlexBetween}
`;

const Name = styled.div`
  ${Typography.body_md}
  color: var(--text-secondary);
  height: 16px;
`;
const Content = styled.div`
  ${Typography.body_md_medium}
  text-align: right;
  color: var(--text-primary);
`;
const TotalAmount = styled.div`
  box-shadow: var(--top-border);
  margin-top: var(--space-4);
  padding-top: var(--space-12);
  ${FlexBetween}
`;
const TotalContent = styled.div`
  ${Typography.heading_md}
  align-items: center;
  color: var(--text-primary);
`;

const Checks = styled.div`
  margin-inline: var(--space-20);

  ${mobile_xl(css`
    margin: var(--space-0);
    padding-block: var(--space-2);
  `)}
`;

const Buttons = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: var(--space-0);
  gap: var(--space-10);
`;

const Price = styled.p`
  ${Typography.body_md_medium}
  color: var(--text-secondary);
  border-right: 0.5px solid var(--border-primary);
  padding-inline: var(--space-12);
  margin: var(--space-0);
  text-transform: uppercase;
  width: 90px;
  text-align: end;
  ${mobile_xl(css`
    padding: var(--space-0);
    text-align: left;
    width: unset;
    border-right: none;
  `)}
`;

const UnitRate = styled.input`
  ${Typography.body_md_medium}
  color: var(--text-primary);
  background: var(--background-primary);
  border: none;
  border-radius: var(--border-radius-4);
  text-align: end;
  width: 90px;
  padding: var(--space-0) var(--space-16) var(--space-0) var(--space-12);
  &:focus-visible {
    outline: none;
  }
  ::placeholder {
    color: var(--text-placeholder);
    ${Typography.body_md_medium}
  }
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: var(--space-0);
  }
  ${mobile_xl(css`
    padding: var(--space-0);
  `)}
`;
const RowRightHeader = styled.div`
  ${AlignCenter}
  ${mobile_xl(css`
    flex-direction: column;
    width: 100%;
  `)}
`;

const RemoveIcon = styled.div`
  width: 24px;
  padding-left: var(--space-12);
  ${desktop_small(css`
    position: sticky;
    right: 0;
    height: 100%;
    background-color: var(--background-tertiary);
    padding: var(--space-10) var(--space-12);
    box-shadow: var(--shadow-card);
  `)}
`;

const DragIcon = styled.div`
  width: 16px;
  height: 16px;
  position: absolute;
  left: 4px;
  .drag-icon {
    visibility: hidden;
    fill: var(--text-secondary);
    cursor: grab;
  }
  ${mobile_xl(css`
    display: none;
  `)}
`;

const ItemRow = styled.div`
  &.first-row {
    display: none;
  }
  .input-field {
    margin-right: var(--space-12);
    input {
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    ${desktop_small(css`
      padding-block: var(--space-8);
    `)}
    ${mobile_xl(css`
      padding: var(--space-0);
      margin-right: var(--space-0);
    `)}
  }
  .angleDown-icon {
    transition: transform var(--transition-normal) ease;
  }
  .angleDown-icon.rotate {
    transform: rotate(180deg);
  }
  ${mobile_xl(css`
    ${FlexBetween}
    gap: var(--space-10);
    padding: var(--space-8);
    border-bottom: 0.5px solid var(--border-primary);
    width: -webkit-fill-available;
    &.first-row {
      display: flex;
      align-items: flex-start;
    }
    :last-child {
      border-bottom: none;
    }
  `)}
`;
const ResponsiveLabel = styled.div`
  display: none;
  ${mobile_xl(css`
    display: block;
    color: var(--text-secondary);
    margin: var(--space-0);
    text-transform: uppercase;
    white-space: nowrap;
    padding: var(--space-0);
  `)}
`;

const TableGroup = styled.div`
  display: flex;
  flex-direction: column;
  ${HideScrollbar}
  border-radius: var(--border-radius-16) var(--border-radius-16) var(--border-radius-0) var(--border-radius-0);
  ${mobile_xl(css`
    border-radius: var(--border-radius-8) var(--border-radius-8) var(--border-radius-0) var(--border-radius-0);
  `)}
`;

const DeleteIcon = styled.div`
  ${desktop_small(css`
    position: sticky;
    right: 0;
    padding: var(--space-18) var(--space-12);
    background: var(--background-primary);
    box-shadow: var(--shadow-card);
    z-index: var(--z-index-header);
  `)}
`;

interface SortableWrapperProps {
  isDragging?: boolean;
  cssTransform?: string | undefined;
  cssTransition?: string | undefined;
}

const SortableWrapper = styled.div<SortableWrapperProps>`
  width: 100%;
  opacity: ${(props) => (props.isDragging ? 0.5 : 1)};
  transform: ${(props) => props.cssTransform || 'none'};
  transition: ${(props) => props.cssTransition || 'none'};
`;

const DragOverlayFields = styled(Fields)`
  pointer-events: none;
`;

export {
  OuterDiv,
  HeaderBlock,
  Heading,
  Icon,
  HeaderTitle,
  MainGroup,
  Group1,
  Row,
  Label,
  InputLabel,
  AnotherTex,
  NoTex,
  Group2,
  GroupHeader,
  RightHeader,
  Project,
  Quantity,
  Amount,
  Fields,
  InputArea,
  QuantityRate,
  AmountRate,
  AddButton,
  BottomField,
  Total,
  Subtotal,
  Name,
  Content,
  TotalAmount,
  TotalContent,
  Buttons,
  Group,
  Price,
  UnitRate,
  RowRightHeader,
  Checks,
  TaxOutside,
  Label1,
  RemoveIcon,
  Tax,
  DragIcon,
  ItemRow,
  ResponsiveLabel,
  TableGroup,
  DeleteIcon,
  SortableWrapper,
  DragOverlayFields
};
